import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import Modal, { ConfirmDialog } from './Modal';
import { FILE_ICONS } from '../data/decisionsData';
import { authHeaders, getAccessToken, isSecretaryContextUser } from '../authStorage';
import { attachmentKindLabel, decisionPriorityLabel, decisionStatusLabel, ATT_TYPES } from '../decisionFormUtils';
import { decisionAttachmentDownloadUrl, fetchDecisionById, sendDecisionWhatsappNotifications } from '../decisionsApi';
import { summarizeDecision } from '../aiApi';
import { fetchBoardMembers } from '../boardApi';
import { changeTaskStatus, createTask, deleteTask, fetchTasks } from '../tasksApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

function DecisionDetailPage() {
  const { language, direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const decisionId = Number(idParam);
  const invalidId = !Number.isFinite(decisionId) || decisionId < 1;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(!invalidId);
  const [loadError, setLoadError] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [waMsg, setWaMsg] = useState('');
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [deleteTaskItem, setDeleteTaskItem] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', assigneeBoardMemberId: '', dueDateUtc: '', priority: 'medium', status: 'new' });
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiCopied, setAiCopied] = useState(false);

  const dateLocale = language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory' : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US';

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t('common.noPerm')); setLoading(false); return; }
    if (invalidId) { setLoadError(t('decisions.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const d = await fetchDecisionById(decisionId);
        const [taskRows, memberRows] = await Promise.all([
          fetchTasks({ linkedType: 'decision', linkedId: decisionId }),
          fetchBoardMembers(),
        ]);
        if (!cancelled) {
          setItem(d);
          setTasks(taskRows);
          setMembers(memberRows.filter((x) => String(x.branchId) === String(d.branchId)));
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, decisionId, invalidId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWhatsapp = async () => {
    if (!item) return;
    setWaMsg('');
    setWaSending(true);
    try {
      const r = await sendDecisionWhatsappNotifications(item.id, 'all');
      const lines = Array.isArray(r.log) ? r.log.join('\n') : '';
      const simTag = r.simulated ? ` (${t('decisions.waSimulated')})` : '';
      setWaMsg(
        `${t('decisions.waSent')}: ${r.sent} — ${t('decisions.waNoPhone')}: ${r.skippedNoPhone} — ${t('decisions.waFailed')}: ${r.failed}${simTag}\n${lines}`.trim(),
      );
    } catch (e) {
      setWaMsg(e instanceof Error ? e.message : t('decisions.waResult'));
    } finally {
      setWaSending(false);
    }
  };

  const openAttachmentDocument = async (attachment) => {
    const openedTab = window.open('about:blank', '_blank');
    if (!openedTab) {
      setLoadError(t('decisions.openFileFailed'));
      return;
    }
    const writeTabHtml = (title, body) => {
      openedTab.document.open();
      openedTab.document.write(`<!DOCTYPE html><html dir="${direction}"><head><meta charset="utf-8"><title>${title}</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;background:#f5fdfb;color:#00334a;}
      h1{font-size:1.1rem;margin:0 0 8px;}p{color:#607d8b;line-height:1.6;}</style></head><body>${body}</body></html>`);
      openedTab.document.close();
    };
    writeTabHtml(attachment.name || 'Attachment', `<h1>${t('decisions.openingFile')}</h1><p>${t('decisions.pleaseWait')}</p>`);

    try {
      const url = decisionAttachmentDownloadUrl(decisionId, attachment.id);
      const res = await fetch(url, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(t('decisions.fileNotFound'));
      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error(t('decisions.openFileFailed'));
      const objectUrl = URL.createObjectURL(blob);
      openedTab.location.replace(objectUrl);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      writeTabHtml(
        attachment.name || 'Attachment',
        `<h1>${t('decisions.openFileFailed')}</h1><p>${e instanceof Error ? e.message : t('decisions.openFileFailed')}</p>`,
      );
      setLoadError(e instanceof Error ? e.message : t('decisions.openFileFailed'));
    }
  };

  const handleAiSummary = async () => {
    if (!item || aiLoading) return;
    setAiError('');
    setAiSummary('');
    setAiLoading(true);
    try {
      const text = await summarizeDecision(item.title, item.description || item.title);
      setAiSummary(text);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : t('decisions.aiError'));
    } finally {
      setAiLoading(false);
    }
  };

  const handleCopyAiSummary = () => {
    if (!aiSummary) return;
    navigator.clipboard.writeText(aiSummary).then(() => {
      setAiCopied(true);
      setTimeout(() => setAiCopied(false), 2000);
    });
  };

  const statusLabel = (v) => (
    v === 'completed' ? t('common.completed') :
    v === 'in_progress' ? t('common.inProgress') :
    v === 'overdue' ? t('common.overdue') :
    t('common.taskStatusNew')
  );
  const priorityLabel = (v) => (
    v === 'high' ? t('decisions.priorityHigh') :
    v === 'low' ? t('decisions.priorityLow') :
    t('decisions.priorityMedium')
  );

  const addTask = async () => {
    if (!item || !taskForm.title.trim()) return;
    setTaskSaving(true);
    try {
      const created = await createTask({
        title: taskForm.title,
        description: null,
        companyId: Number(item.branchId),
        assigneeBoardMemberId: taskForm.assigneeBoardMemberId ? Number(taskForm.assigneeBoardMemberId) : null,
        dueDateUtc: taskForm.dueDateUtc || null,
        priority: taskForm.priority,
        status: taskForm.status,
        linkedMeetingId: null,
        linkedDecisionId: item.id,
      });
      setTasks((p) => [created, ...p]);
      setTaskOpen(false);
      setTaskForm({ title: '', assigneeBoardMemberId: '', dueDateUtc: '', priority: 'medium', status: 'new' });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
    } finally {
      setTaskSaving(false);
    }
  };

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <button type="button" className="btn btn--ghost" onClick={() => navigate(`${basePath}/decisions`)}><Icon name="chevron-right" size={14} /> {t('common.back')}</button>
              <span className="list-page-card__title"><Icon name="decisions" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('decisions.pageTitle')}</span>
            </div>
            <span className="list-page-card__sub">{item?.title || ''}</span>
          </div>
          {item && (
            <div className="list-page-card__actions">
              {item.status === 'pending' && (
                <button type="button" className="btn btn--outline" disabled={waSending} onClick={handleWhatsapp} title={t('decisions.simHint')}>
                  <Icon name="voting" size={14} color="#25D366" /> {t('decisions.sendWhatsapp')}
                </button>
              )}
              <button type="button" className="btn btn--primary" onClick={() => navigate(`${basePath}/decisions/${item.id}/edit`)}><Icon name="edit" size={14} color="#fff" /> {t('decisions.editDecision')}</button>
            </div>
          )}
        </header>
        {loadError && <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>}
        {waMsg && (
          <div className="list-page-card__banner" style={{ background: '#e8f5e9', color: '#1b5e20', whiteSpace: 'pre-wrap', borderColor: '#c8e6c9' }} dir="auto">
            {waMsg}
          </div>
        )}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}
      {!loading && item && (
        <div className="table-card" style={{ padding: '20px 22px' }}>
          <div className="mform">
            <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('decisions.lblTitle')}</label><div className="mform__input-wrap"><Icon name="decisions" size={14} color="#02c3a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.title || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblCreator')}</label><div className="mform__input-wrap"><Icon name="user" size={14} color="#455a64" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.creator || '—'} dir="auto" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblBranch')}</label><div className="mform__input-wrap"><Icon name="branch" size={14} color="#1b8eff" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.branch || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblDecisionType')}</label><div className="mform__input-wrap"><Icon name="filter" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.decisionType || ''} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblCommittee')}</label><div className="mform__input-wrap"><Icon name="board" size={14} color="#546e7a" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.committee || '—'} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblPriority')}</label><div className="mform__input-wrap"><Icon name="warning" size={14} color="#ff8c42" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={decisionPriorityLabel(item.priority, language)} disabled /></div></div>
            </div>
            <div className="mform__row mform__row--4">
              <div className="mform__field mform__field--span3"><label className="mform__label">{t('decisions.lblDescription')}</label><div className="mform__input-wrap"><Icon name="report" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.description || ''} disabled /></div></div>
            </div>
            <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('decisions.lblStatus')}</label><div className="mform__input-wrap"><Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={decisionStatusLabel(item.status, language)} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblDeadline')}</label><div className="mform__input-wrap"><Icon name="calendar" size={14} color="#0ea5a4" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.deadline || ''} dir="ltr" disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblTotalMembers')}</label><div className="mform__input-wrap"><Icon name="board" size={14} color="#14b8a6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.totalMembers || 0} disabled /></div></div>
              <div className="mform__field"><label className="mform__label">{t('decisions.lblCreatedAt')}</label><div className="mform__input-wrap"><Icon name="calendar" size={14} color="#ff8c42" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" value={item.date || ''} dir="ltr" disabled /></div></div>
            </div>
            {/* AI Summary Section */}
            <div style={{ margin: '18px 0', border: '1.5px solid #e0f7f6', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(90deg,#0EA5A0 0%,#14b8a6 100%)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: '1.1rem' }}>✨</span> {t('decisions.aiSummary')}
                </span>
                <button
                  type="button"
                  onClick={handleAiSummary}
                  disabled={aiLoading}
                  style={{ background: '#fff', color: '#0EA5A0', border: 'none', borderRadius: 6, padding: '5px 14px', fontWeight: 700, cursor: aiLoading ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontFamily: 'inherit', opacity: aiLoading ? 0.7 : 1 }}
                >
                  {aiLoading ? t('decisions.aiSummarizing') : t('decisions.aiSummarize')}
                </button>
              </div>
              <div style={{ padding: '12px 16px', background: '#f8fffe', minHeight: 60 }}>
                {!aiSummary && !aiError && !aiLoading && (
                  <p style={{ color: '#90a4ae', margin: 0, fontSize: '0.88rem' }}>{t('decisions.aiSummaryHint')}</p>
                )}
                {aiLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#0EA5A0' }}>
                    <span style={{ fontSize: '1.2rem', animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                    <span style={{ fontWeight: 600 }}>{t('decisions.aiSummarizing')}</span>
                  </div>
                )}
                {aiError && <p style={{ color: '#c62828', margin: 0 }}>{aiError}</p>}
                {aiSummary && (
                  <>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: '#1a3c40', fontSize: '0.93rem' }}>{aiSummary}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                      <button
                        type="button"
                        onClick={handleCopyAiSummary}
                        style={{ background: aiCopied ? '#0EA5A0' : '#f0fffe', color: aiCopied ? '#fff' : '#0EA5A0', border: '1px solid #0EA5A0', borderRadius: 6, padding: '4px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s' }}
                      >
                        {aiCopied ? `✓ ${t('decisions.aiCopied')}` : t('decisions.aiCopy')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mform__section-title"><Icon name="report" size={13} color="#02c3a6" /> {t('decisions.sectionAttachments')} <span className="mform__section-badge">{(item.attachments || []).length}</span></div>
            {(item.attachments || []).length > 0 && (
              <div className="mform__att-list">
                {item.attachments.map((att, i) => (
                  <div key={`${att.name}-${i}`} className="mform__att-item">
                    <span className="branch-view__contract-icon" style={{ background: (FILE_ICONS[att.type] ?? FILE_ICONS.pdf).bg }}>
                      {(FILE_ICONS[att.type] ?? FILE_ICONS.pdf).icon}
                    </span>
                    <span className="mform__att-type">{attachmentKindLabel(att.type || ATT_TYPES[0].value, language)}</span>
                    <span className="mform__att-name">{att.name}</span>
                    <span style={{ fontSize: '0.72rem', color: '#90a4ae' }}>{att.size || '—'}</span>
                    <button
                      type="button"
                      className="btn btn--outline branch-view__open-btn"
                      onClick={() => openAttachmentDocument(att)}
                      disabled={!att.id}
                    >
                      <Icon name="eye" size={14} color="#02c3a6" /> {t('decisions.openPreview')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mform__section-title"><Icon name="settings" size={13} color="#8b5cf6" /> {t('decisions.tasks')} <span className="mform__section-badge">{tasks.length}</span></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button type="button" className="btn btn--outline" onClick={() => setTaskOpen(true)}><Icon name="plus" size={14} color="#02c3a6" /> {t('decisions.addTask')}</button>
            </div>
            <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
              <table className="data-table">
                <thead><tr><th>{t('decisions.taskTitle')}</th><th>{t('decisions.assignee')}</th><th>{t('decisions.priority')}</th><th>{t('decisions.taskStatus')}</th><th>{t('decisions.dueDate')}</th><th>{t('common.actions')}</th></tr></thead>
                <tbody>
                  {tasks.map((x) => (
                    <tr key={x.id}>
                      <td>{x.title}</td>
                      <td>{x.assigneeName || '—'}</td>
                      <td>{priorityLabel(x.priority)}</td>
                      <td style={{ color: x.status === 'overdue' ? '#c62828' : 'inherit', fontWeight: 700 }}>{statusLabel(x.status)}</td>
                      <td>{x.dueDateUtc ? new Date(x.dueDateUtc).toLocaleDateString(dateLocale) : '—'}</td>
                      <td><div className="td-actions">
                        <button type="button" className="action-btn action-btn--edit" onClick={async () => {
                          const next = x.status === 'completed' ? 'in_progress' : 'completed';
                          await changeTaskStatus(x.id, next);
                          setTasks((p) => p.map((r) => (r.id === x.id ? { ...r, status: next } : r)));
                        }}><Icon name="check-circle" size={14} /></button>
                        <button type="button" className="action-btn action-btn--delete" onClick={() => setDeleteTaskItem(x)}><Icon name="trash" size={14} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tasks.length === 0 && <div className="users-empty">{t('decisions.noTasks')}</div>}
            </div>
          </div>
        </div>
      )}
      </div>

      <Modal open={taskOpen} onClose={() => setTaskOpen(false)} title={t('decisions.addTask')} subtitle={t('decisions.addTaskSubtitle')} size="lg">
        <div className="mform">
          <div className="mform__field"><label className="mform__label">{t('decisions.taskTitle')} <span>*</span></label><input className="mform__input" value={taskForm.title} onChange={(e) => setTaskForm((p) => ({ ...p, title: e.target.value }))} /></div>
          <div className="mform__row mform__row--4">
            <div className="mform__field"><label className="mform__label">{t('decisions.assignee')}</label><select className="mform__select" value={taskForm.assigneeBoardMemberId} onChange={(e) => setTaskForm((p) => ({ ...p, assigneeBoardMemberId: e.target.value }))}><option value="">{t('common.none')}</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
            <div className="mform__field"><label className="mform__label">{t('decisions.dueDate')}</label><input type="date" className="mform__input" value={taskForm.dueDateUtc} onChange={(e) => setTaskForm((p) => ({ ...p, dueDateUtc: e.target.value }))} /></div>
            <div className="mform__field"><label className="mform__label">{t('decisions.priority')}</label><select className="mform__select" value={taskForm.priority} onChange={(e) => setTaskForm((p) => ({ ...p, priority: e.target.value }))}><option value="high">{t('decisions.priorityHigh')}</option><option value="medium">{t('decisions.priorityMedium')}</option><option value="low">{t('decisions.priorityLow')}</option></select></div>
            <div className="mform__field"><label className="mform__label">{t('decisions.taskStatus')}</label><select className="mform__select" value={taskForm.status} onChange={(e) => setTaskForm((p) => ({ ...p, status: e.target.value }))}><option value="new">{t('common.taskStatusNew')}</option><option value="in_progress">{t('common.inProgress')}</option><option value="completed">{t('common.completed')}</option></select></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn--ghost" onClick={() => setTaskOpen(false)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn--primary" onClick={addTask} disabled={taskSaving}>{taskSaving ? t('common.loading') : t('common.save')}</button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTaskItem}
        onClose={() => setDeleteTaskItem(null)}
        onConfirm={async () => {
          if (!deleteTaskItem) return;
          await deleteTask(deleteTaskItem.id);
          setTasks((p) => p.filter((x) => x.id !== deleteTaskItem.id));
          setDeleteTaskItem(null);
        }}
        itemName={deleteTaskItem?.title}
        title={t('decisions.deleteTitle')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
}

export default DecisionDetailPage;
